import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useCoachStore, JuniorCoach } from '@store/coachStore';
import { useAuthStore } from '@store/authStore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const JuniorCoachManagement: React.FC = () => {
  const { juniorCoaches, setJuniorCoaches, addJuniorCoach, updateJuniorCoach, deleteJuniorCoach } = useCoachStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCoach, setEditingCoach] = useState<JuniorCoach | null>(null);
  const [formData, setFormData] = useState({
    coachIdCode: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    assigned_sports: [] as string[],
  });
  const [searchingCoach, setSearchingCoach] = useState(false);
  const [foundCoach, setFoundCoach] = useState<any>(null);

  const sportsOptions = ['Badminton', 'Tennis', 'Table Tennis', 'Squash'];

  useEffect(() => {
    loadJuniorCoaches();
  }, []);

  // Auto-refresh coaches list when page becomes visible (e.g., after tab switch or window focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Page visible - refreshing junior coaches list...');
        loadJuniorCoaches();
      }
    };

    const handleFocus = () => {
      console.log('🔄 Window focused - refreshing junior coaches list...');
      loadJuniorCoaches();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Debug form data changes
  useEffect(() => {
    // console.log('Form data changed:', formData);
  }, [formData]);

  const loadJuniorCoaches = async () => {
    setLoading(true);
    try {
      // Get coachId from auth store
      const { user } = useAuthStore.getState();
      let coachId = (user as any)?.coachId;
      
      if (!coachId && user?.id) {
        try {
          const response = await fetch(`/api/coaches/me?userId=${user.id}`);
          const data = await response.json();
          if (data.success && data.data) {
            coachId = data.data.id;
          }
        } catch (err) {
          console.error('Error fetching coach data:', err);
        }
      }
      
      if (!coachId) {
        setError('Coach ID not found');
        setLoading(false);
        return;
      }

      // Load junior coaches from API
      const response = await fetch(`/api/coaches/hierarchy/${coachId}`);
      const data = await response.json();
      
      if (data.success && data.data?.juniorCoaches) {
        const juniorCoaches = data.data.juniorCoaches.map((coach: any) => ({
          id: coach.id,
          coach_id_code: coach.coachIdCode || coach.coach_id_code,
          first_name: coach.firstName || coach.first_name,
          last_name: coach.lastName || coach.last_name,
          email: coach.user?.email || coach.email,
          assigned_sports: coach.assignedSports || coach.assigned_sports || [],
          coach_level: coach.coachLevel || coach.coach_level || 'junior_coach',
          academy_id: coach.academyId || coach.academy_id,
          is_active: coach.user?.isActive !== false,
          created_at: coach.createdAt || coach.created_at,
          updated_at: coach.updatedAt || coach.updated_at,
        }));
        setJuniorCoaches(juniorCoaches);
      } else {
        setJuniorCoaches([]);
      }
    } catch (err: any) {
      console.error('Error loading junior coaches:', err);
      setError(err.message || 'Failed to load junior coaches');
      setJuniorCoaches([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/coach/dashboard');
  };

  const handleOpenDialog = (coach?: JuniorCoach) => {
    // console.log('Opening dialog for coach:', coach);
    if (coach) {
      setEditingCoach(coach);
      // Reset form data completely before setting new values
      setFormData({
        coachIdCode: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        assigned_sports: [],
      });
      // Use setTimeout to ensure state is reset before setting new values
      setTimeout(() => {
        setFormData({
          first_name: coach.first_name,
          last_name: coach.last_name,
          email: coach.email,
          password: '',
          assigned_sports: [...coach.assigned_sports],
        });
        // console.log('Form data set after timeout:', {
        //   first_name: coach.first_name,
        //   last_name: coach.last_name,
        //   email: coach.email,
        //   password: '',
        //   assigned_sports: [...coach.assigned_sports],
        // });
      }, 0);
    } else {
      setEditingCoach(null);
      setFormData({
        coachIdCode: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        assigned_sports: [],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCoach(null);
    setFormData({
      coachIdCode: '',
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      assigned_sports: [],
    });
    setFoundCoach(null);
  };

  const handleSearchByCoachId = async () => {
    if (!formData.coachIdCode) {
      toast.error('Please enter a Coach ID');
      return;
    }

    setSearchingCoach(true);
    try {
      const response = await fetch(`/api/coaches/by-id/${formData.coachIdCode}`);
      const result = await response.json();
      
      if (result.success && result.data) {
        const coach = result.data;
        setFormData({
          ...formData,
          first_name: coach.firstName || coach.first_name || formData.first_name,
          last_name: coach.lastName || coach.last_name || formData.last_name,
          email: coach.email || formData.email,
          assigned_sports: coach.assignedSports || coach.assigned_sports || formData.assigned_sports,
        });
        setFoundCoach(coach);
        toast.success('Coach information loaded successfully');
      } else {
        toast.error(result.message || 'Coach not found');
        setFoundCoach(null);
      }
    } catch (err: any) {
      console.error('Error searching coach:', err);
      toast.error('Failed to search coach');
      setFoundCoach(null);
    } finally {
      setSearchingCoach(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const { user } = useAuthStore.getState();
      let coachId = (user as any)?.coachId;
      
      if (!coachId && user?.id) {
        try {
          const response = await fetch(`/api/coaches/me?userId=${user.id}`);
          const data = await response.json();
          if (data.success && data.data) {
            coachId = data.data.id;
          }
        } catch (err) {
          console.error('Error fetching coach data:', err);
        }
      }

      if (!coachId) {
        setError('Coach ID not found. Please contact support.');
        return;
      }

      setLoading(true);

      if (editingCoach) {
        // TODO: Implement update API endpoint
        setError('Update functionality not yet implemented');
        setLoading(false);
        return;
      } else {
        // Add new junior coach via API
        const response = await fetch(`/api/coaches/${coachId}/junior-coaches`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            firstName: formData.first_name,
            lastName: formData.last_name,
            email: formData.email,
            phone: '',
            assignedSports: formData.assigned_sports,
            coachIdCode: formData.coachIdCode || undefined,
          }),
        });

        const result = await response.json();

        if (result.success) {
          toast.success('Junior coach added successfully!');
          handleCloseDialog();
          loadJuniorCoaches(); // Refresh the list from API
        } else {
          setError(result.message || 'Failed to add junior coach');
        }
      }
    } catch (err: any) {
      console.error('Error saving junior coach:', err);
      setError(err.message || 'Failed to save junior coach');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (coachId: string) => {
    if (window.confirm('Are you sure you want to delete this junior coach?')) {
      try {
        // TODO: Implement actual API call
        console.log('Deleting junior coach:', coachId);
        deleteJuniorCoach(coachId);
        setSuccess('Junior coach deleted successfully!');
      } catch (err: any) {
        setError(err.message || 'Failed to delete junior coach');
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <Typography variant="h4" component="h1">
            Junior Coach Management
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              console.log('🔄 Manual refresh triggered');
              loadJuniorCoaches();
            }}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Junior Coach
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : juniorCoaches.length === 0 ? (
            <Alert severity="info">No junior coaches found. Add your first junior coach to get started.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Coach ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Assigned Sports</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {juniorCoaches.map((coach) => (
                    <TableRow key={coach.id}>
                      <TableCell>{coach.coach_id_code}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {coach.first_name} {coach.last_name}
                        </Box>
                      </TableCell>
                      <TableCell>{coach.email}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {coach.assigned_sports && coach.assigned_sports.length > 0 ? (
                            coach.assigned_sports.map((sport) => (
                              <Chip key={sport} label={sport} size="small" color="primary" />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">None</Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Coach">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(coach)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Junior Coach Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCoach ? 'Edit Junior Coach' : 'Add New Junior Coach'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!editingCoach && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                  <TextField
                    fullWidth
                    label="EncycGames Coach ID (Optional)"
                    value={formData.coachIdCode}
                    onChange={(e) => setFormData({ ...formData, coachIdCode: e.target.value })}
                    helperText="If coach is already registered, enter their Coach ID here"
                  />
                  <Button
                    variant="outlined"
                    onClick={handleSearchByCoachId}
                    disabled={!formData.coachIdCode || searchingCoach}
                  >
                    {searchingCoach ? 'Searching...' : 'Search & Load'}
                  </Button>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            {!editingCoach && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assigned Sports</InputLabel>
                <Select
                  multiple
                  value={formData.assigned_sports}
                  onChange={(e) => {
                    const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                    setFormData({
                      ...formData,
                      assigned_sports: value
                    });
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {sportsOptions.map((sport) => (
                    <MenuItem key={sport} value={sport}>
                      <Checkbox checked={formData.assigned_sports.indexOf(sport) > -1} />
                      {sport}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCoach ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default JuniorCoachManagement;
